import React from 'react';
import {
  Link, Heading, useBreakpoint,
  type CardProps,
} from '@printdeal/ui-library';
import { Link as GatsbyLink } from 'gatsby';
import cx from 'clsx';
import {
  ProductProps,
  ImageProps,
  ProductListImpression,
  ProductListOnClick,
} from './types';
import { ProductList } from './ProductList';
import { FeaturedProductList } from './featured-products/FeaturedProductList';
import { ExploreDesignTemplateButton } from '../../design-template/exploreDesignTemplateButton';
import type { AudiencesData } from '../../../templates/homepage/homepage-helper';
import { ConsentData } from '../../../helpers/CookiebotHelper';

interface ProductSectionProps extends ProductProps {
  onClick?: ProductListOnClick;
  onImpression?: ProductListImpression;
  onLandingPageClick?: () => void;
  maxItems?: number;
  columns?: 3 | 4;
  cardVariant?: CardProps['variant'];
  twoColumnBreakpoint?: Parameters<typeof useBreakpoint>[0];
  isFeaturedProductsList?: Boolean;
  isPopularProductList?: Boolean;
  imagePriority?: ImageProps['imagePriority'];
  showLoginOrRecentOrders?: Boolean;
  audiencesDataFromSegment?: AudiencesData;
  audienceOverRideForPlayground?: string | null | undefined;
  showHeader?: boolean;
  consent?: ConsentData;
}

export const ProductSection = (props: ProductSectionProps) => {
  const {
    landingPage,
    landingPageLinkText,
    list,
    maxItems = 12,
    columns,
    onClick,
    onImpression,
    onLandingPageClick,
    title,
    cardVariant,
    twoColumnBreakpoint,
    isFeaturedProductsList = false,
    isPopularProductList = false,
    imagePriority = false,
    showLoginOrRecentOrders = false,
    audiencesDataFromSegment,
    audienceOverRideForPlayground,
    showHeader = true,
    consent,
  } = props;

  if (!list) return null;

  return (
    <>
      {showHeader
        && (
          <header className={cx('pd-mb-6 pd-flex', {
            'pd-gap-6': isPopularProductList,
          })}
          >
            {title && (
              <Heading
                as="div"
                size="2"
                className={cx('pd-mb-0', {
                  'pd-grow': !isPopularProductList,
                })}
              >
                {title}
              </Heading>
            )}
            {isPopularProductList && (
              <ExploreDesignTemplateButton label="home_button" isButton={false} />
            )}
            {landingPageLinkText && landingPage?.path && (
              <Link
                to={landingPage.path}
                onClick={onLandingPageClick}
                variant="secondary"
                iconAfter="chevron-right"
                className="pd-text-sm"
                as={GatsbyLink}
              >
                {landingPageLinkText}
              </Link>
            )}
          </header>
        )}
      {isFeaturedProductsList
        ? (
          <FeaturedProductList
            list={list}
            maxItems={maxItems}
            columns={columns}
            handleClick={onClick}
            impressionHandler={onImpression}
            cardVariant={cardVariant}
            twoColumnBreakpoint={twoColumnBreakpoint}
            imagePriority={imagePriority}
            showLoginOrRecentOrders={showLoginOrRecentOrders}
            audiencesDataFromSegment={audiencesDataFromSegment}
            audienceOverRideForPlayground={audienceOverRideForPlayground}
            /**
             * Resetting additional padding temporarily
             * till we get the new variant of cards with Vertical variant
             * As we have experiments running and we need visible product images
             * Only grid gaps are enough for now
             */
            className="pd-px-0"
            consentData={consent}
          />
        )
        : (
          <ProductList
            list={list}
            maxItems={maxItems}
            columns={columns}
            clickHandler={onClick}
            impressionHandler={onImpression}
            cardVariant={cardVariant}
            twoColumnBreakpoint={twoColumnBreakpoint}
            imagePriority={imagePriority}
          />
        )}
    </>
  );
};
